// Reading a question aloud.
//
// Synthesis happens on the server, not in the browser. `SpeechSynthesis` is
// blocked outright by privacy-hardened browsers and elsewhere uses whatever OS
// voice happens to be installed, which is usually robotic and often not German.
// One clear voice everywhere is part of what this product is, so the server
// renders the audio and the browser plays it.
//
// The route takes a message *index*, not text. The server already holds the
// transcript, and sending it back to be spoken would let a client have any
// sentence read in the tutor's voice.

import { raw, type Credential } from '../api/client';

export const speechSupported = typeof window !== 'undefined' && typeof Audio !== 'undefined';

let stopCurrent: (() => void) | null = null;

/** Stops whatever is playing. Called before a new utterance, and on leaving. */
export function cancelSpeech(): void {
  const stop = stopCurrent;
  stopCurrent = null;
  stop?.();
}

/**
 * Speaks one message of a session and resolves when it has finished playing.
 * A failure resolves quietly: a tutor whose voice hiccups should still be a
 * tutor, and the text is on screen either way.
 */
export async function speak(
  sessionId: string,
  index: number,
  ctx: { orgId: string; credential: Credential | null },
): Promise<void> {
  if (!speechSupported) return;
  cancelSpeech();

  let url: string;
  try {
    const response = await raw(`/sessions/${sessionId}/speak`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ index }),
      orgId: ctx.orgId,
      credential: ctx.credential,
    });
    url = URL.createObjectURL(await response.blob());
  } catch {
    return;
  }

  await new Promise<void>((resolve) => {
    const audio = new Audio(url);
    const done = () => {
      URL.revokeObjectURL(url);
      if (stopCurrent === stop) stopCurrent = null;
      resolve();
    };
    const stop = () => {
      audio.pause();
      done();
    };
    stopCurrent = stop;
    audio.onended = done;
    audio.onerror = done;
    void audio.play().catch(done);
  });
}
